import { useState } from 'react'
import { useRoomViewStore } from 'store/roomView/roomView.store'
import { EPatientStatus, TRoomViewScheduledSurgery } from '../RoomView.types'
import { TAddPatientMenuProps } from './AddPatientMenu.types'
import { useUpdatePatientStatusMutation } from '../hooks/surgeries.hook'
import { enqueueSnackbar } from 'notistack'

export const useAddPatientMenuLogic = (props: TAddPatientMenuProps) => {
  const { onClose, status, roomId, showTabs = false } = props
  const { scheduledSurgeries, roomViewSettings, refetchAll } =
    useRoomViewStore()
  const [updatePatientStatus] = useUpdatePatientStatusMutation()

  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState(0)

  const waitingPatients = scheduledSurgeries
    .filter(
      (surgery) =>
        surgery.patientStatusHistory.length > 0 &&
        surgery.patientStatusHistory[0]?.status === EPatientStatus.CHECKED_IN
    )
    .sort((a, b) => {
      const dateA = new Date(a.procedure.dateTime)
      const dateB = new Date(b.procedure.dateTime)
      return dateA.getTime() - dateB.getTime()
    })

  const scheduledPatients = scheduledSurgeries
    .filter(
      (surgery) =>
        surgery.patientStatusHistory.length === 0 ||
        surgery.patientStatusHistory[0]?.status === EPatientStatus.SCHEDULED
    )
    .sort((a, b) => {
      const dateA = new Date(a.procedure.dateTime)
      const dateB = new Date(b.procedure.dateTime)
      return dateA.getTime() - dateB.getTime()
    })

  const allPatients = showTabs
    ? []
    : [...waitingPatients, ...scheduledPatients].filter(
        (surgery) => surgery.patientStatusHistory[0]?.status !== status
      )

  const filteredWaitingPatients = waitingPatients.filter(
    (surgery) =>
      surgery.patient.firstName
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      surgery.patient.lastName.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const filteredScheduledPatients = scheduledPatients.filter(
    (surgery) =>
      surgery.patient.firstName
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      surgery.patient.lastName.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const filteredAllPatients = allPatients.filter(
    (surgery) =>
      surgery.patient.firstName
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      surgery.patient.lastName.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const currentTabPatients = showTabs
    ? activeTab === 0
      ? filteredWaitingPatients
      : filteredScheduledPatients
    : filteredAllPatients

  const getEmptyStateMessage = () => {
    if (!showTabs) {
      return 'No patients found'
    }
    return activeTab === 0
      ? 'No waiting patients found'
      : 'No scheduled patients found'
  }

  const getSearchPlaceholder = () => {
    if (!showTabs) {
      return 'Search patients...'
    }
    return `Search ${activeTab === 0 ? 'waiting' : 'scheduled'} patients...`
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
    setSearchQuery('')
  }

  const handleClose = () => {
    setActiveTab(0)
    setSearchQuery('')
    onClose()
  }

  const handleAddPatient = async (surgery: TRoomViewScheduledSurgery) => {
    try {
      await updatePatientStatus({
        variables: {
          procedureId: surgery._id,
          patientStatus: status,
          roomId,
        },
      })
      refetchAll()
      enqueueSnackbar('Patient added to room', {
        variant: 'success',
      })
    } catch (error: unknown) {
      enqueueSnackbar((error as Error).message, {
        variant: 'error',
      })
    }
    handleClose()
  }

  return {
    searchQuery,
    activeTab,
    showTabs,
    roomViewSettings,
    currentTabPatients,
    filteredWaitingPatients,
    filteredScheduledPatients,
    filteredAllPatients,
    setSearchQuery,
    handleTabChange,
    handleClose,
    handleAddPatient,
    getEmptyStateMessage,
    getSearchPlaceholder,
  }
}
